import React, { useState, useCallback } from 'react';
import { Card, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { RouteAnalysisResponse } from '../../models';
import { analyzeRoute } from '../../api/Api';
import { useLocaleContext } from '../../i18n';
import RouteElevationChart from './RouteElevationChart';
import PlannerNavigation from './PlannerNavigation';

interface Props {
  initialRoute?: RouteAnalysisResponse | null;
  onComplete: (route: RouteAnalysisResponse) => void;
}

export default function GpxUpload({ initialRoute, onComplete }: Props) {
  const { locale } = useLocaleContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RouteAnalysisResponse | null>(initialRoute ?? null);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFile = useCallback(async (f: File) => {
    setFileName(f.name);
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const route = await analyzeRoute(f);
      setResult(route);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to analyze route');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [handleFile]
  );

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            className={`border rounded p-4 text-center mb-3 ${dragging ? 'border-primary' : 'border-secondary'}`}
            style={{ borderStyle: 'dashed', cursor: 'pointer' }}
            onClick={() => document.getElementById('gpx-input')?.click()}
          >
            {loading ? (
              <span className="text-secondary"><Spinner size="sm" className="me-2" />{locale.planner.gpx.analyzing}</span>
            ) : fileName ? (
              <span className="text-light">{fileName}</span>
            ) : (
              <span className="text-secondary">{locale.planner.gpx.uploadPrompt}</span>
            )}
          </div>

          <input
            id="gpx-input"
            type="file"
            accept=".gpx"
            className="d-none"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />

          {error && <Alert variant="danger">{error}</Alert>}

          {result && (
            <Card className="mb-3" bg="dark">
              <Card.Body>
                <Row className="text-center mb-3">
                  <Col>
                    <div className="text-light fw-bold">{result.totalDistanceKm.toFixed(1)} km</div>
                    <small className="text-secondary">{locale.planner.gpx.distance}</small>
                  </Col>
                  <Col>
                    <div className="text-light fw-bold">{result.totalElevationGainM.toFixed(0)} m</div>
                    <small className="text-secondary">{locale.planner.gpx.elevationGain}</small>
                  </Col>
                  <Col>
                    <div className="text-light fw-bold">{result.totalElevationLossM.toFixed(0)} m</div>
                    <small className="text-secondary">{locale.planner.gpx.elevationLoss}</small>
                  </Col>
                  <Col>
                    <div className="text-light fw-bold">{result.segments.length}</div>
                    <small className="text-secondary">{locale.planner.gpx.segments}</small>
                  </Col>
                </Row>
                <RouteElevationChart segments={result.segments} />
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>

      {result && (
        <PlannerNavigation
          onNext={() => onComplete(result)}
        />
      )}
    </>
  );
}
